import { identity, noop } from 'lodash-es';
import React from 'react';
import styled from 'styled-components';
import { useCombobox } from 'downshift';

import { Input as BaseInput, sharedInputDefaults } from '../fj-input';

const Wrapper = styled.div`
  position: relative;
`;

const List = styled.ul<{ $open: boolean }>`
  position: absolute;
  top: calc(100% - 1px); // Positioned below input, overlapping bottom border by 1px
  z-index: 1;

  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
  // Only show the bordered box when there is something to show, matching the
  // previous react-autocomplete behavior (empty menu rendered nothing).
  border: ${({ $open }) => ($open ? '1px solid #231f20' : 'none')};
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

const ListEntry = styled.li<{ $isHighlighted: boolean }>`
  list-style-type: none;
  padding: 5px 10px;
  cursor: pointer;
  background: ${({ $isHighlighted }) => ($isHighlighted ? 'rgb(247, 247, 247)' : 'white')};
`;

type ItemValue = string | number;

interface FjTypeaheadProps<TItem> {
  /** typeahead options */
  items?: TItem[];
  /** controlled input value */
  value?: string;
  /** item -> value (React key + onSelect arg) */
  getItemValue?: (item: TItem) => ItemValue;
  /** item -> displayed label */
  getItemLabel?: (item: TItem) => React.ReactNode;
  /** fired with the input string as the user types */
  onChange?: (value: string) => void;
  /** fired with (value, item) when an option is chosen */
  onSelect?: (value: ItemValue, item: TItem) => void;
  /** fired when the input loses focus */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  className?: string;
  placeholder?: string;
  fontSize?: string | number;
  height?: string | number | null;
  borderColor?: string;
}

/**
 * Email/typeahead combobox backed by downshift's `useCombobox` (replaces the
 * unmaintained, React-19-incompatible react-autocomplete). The prop surface is
 * preserved so existing callers -- `<Input typeahead />` in styled-primitives
 * and the license-modal email lookup -- keep working unchanged.
 */
const FjTypeahead = <TItem,>({
  items = [],
  value = '',
  // identity is lodash-es' generic identity; casting narrows it to the item -> value/label
  // shape callers rely on when they don't supply their own accessor (string items).
  getItemValue = identity as unknown as (item: TItem) => ItemValue,
  getItemLabel = identity as unknown as (item: TItem) => React.ReactNode,
  onChange = noop,
  onSelect = noop,
  onBlur = noop,
  className,
  placeholder = '',
  fontSize = sharedInputDefaults.fontSize,
  height = null,
  borderColor = sharedInputDefaults.borderColor,
}: FjTypeaheadProps<TItem>) => {
  const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex } =
    useCombobox<TItem>({
      items,
      inputValue: value,
      itemToString: (item: TItem | null) => (item ? String(getItemValue(item)) : ''),
      onInputValueChange: ({ inputValue, type }) => {
        // Only forward genuine typing -- not the input value downshift writes back
        // on selection or on a controlled-value sync -- to match react-autocomplete's
        // onChange contract and avoid a feedback loop with the controlled `value`.
        if (type === useCombobox.stateChangeTypes.InputChange) {
          onChange(inputValue ?? '');
        }
      },
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem != null) {
          onSelect(getItemValue(selectedItem), selectedItem);
        }
      },
    });

  const showMenu = isOpen && items.length > 0;

  return (
    <Wrapper className={className}>
      <BaseInput
        {...getInputProps({ onBlur })}
        isValid
        className="FjInput"
        placeholder={placeholder}
        height={height ?? undefined}
        borderColor={borderColor}
        fontSize={fontSize}
      />
      {/* getMenuProps must always be rendered, even when closed, or downshift warns. */}
      <List {...getMenuProps()} $open={showMenu}>
        {showMenu
          ? items.map((item, index) => (
              <ListEntry
                key={getItemValue(item)}
                $isHighlighted={highlightedIndex === index}
                {...getItemProps({ item, index })}
              >
                {getItemLabel(item)}
              </ListEntry>
            ))
          : null}
      </List>
    </Wrapper>
  );
};

export default FjTypeahead;
